import React, { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { IconButton, Tooltip } from '@mui/material';
import { UserInfo } from '../../../common/model/dto/user-info';
import { Resource } from '../../../common/model/type/resource.enum';
import { isDeactivatedAccount } from '../../../common/utils/services/user-list-utils';
import Colours from '../../../design-system/colours';
import { useAdminUserMutations } from '../../../hooks/use-admin-user-mutations';
import useAuthorized from '../../../hooks/use-authorized';
import OvConfirmationDialog from './OvConfirmationDialog';
import OvConfirmationInput from './OvConfirmationInput';
import OvInfoModal from './OvInfoModal';
import OvResetPasswordDialog from './OvResetPasswordDialog';

const stopRowNavigation = (event: MouseEvent) => event.stopPropagation();

const OvUserRowActions: FC<{ user: UserInfo }> = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToUserList = () => navigate('/users');
  const { deactivateUser, deleteUser } = useAdminUserMutations(user.id ?? '');
  const applicationOperations = useAuthorized(Resource.Application);
  const userOperations = useAuthorized(Resource.User);
  const updateAllowed =
    userOperations.update || applicationOperations.supervise;
  const deleteAllowed =
    userOperations.delete || applicationOperations.supervise;

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);
  const [isConfirmInputOpen, setIsConfirmInputOpen] = useState(false);
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  return (
    <Actions onClick={stopRowNavigation}>
      {!isDeactivatedAccount(user) && user.document_id && updateAllowed && (
        <Tooltip title={t('userDetails.actions.deactivateTitle')}>
          <StyledIconButton
            aria-label={t('userDetails.actions.deactivateTitle')}
            onClick={() => setIsDeactivateOpen(true)}
          >
            <PersonRemoveIcon />
          </StyledIconButton>
        </Tooltip>
      )}

      {!user.document_id && deleteAllowed && (
        <Tooltip title={t('userDetails.actions.deleteTitle')}>
          <StyledIconButton
            aria-label={t('userDetails.actions.deleteTitle')}
            onClick={() => setIsDeleteOpen(true)}
          >
            <DeleteIcon />
          </StyledIconButton>
        </Tooltip>
      )}

      {updateAllowed && (
        <Tooltip title={t('userDetails.actions.resetPasswordTitle')}>
          <StyledIconButton
            aria-label={t('userDetails.actions.resetPasswordTitle')}
            onClick={() => setIsResetPasswordOpen(true)}
          >
            <EmailIcon />
          </StyledIconButton>
        </Tooltip>
      )}

      <OvConfirmationDialog
        icon={<DialogIcon as={DeleteIcon} />}
        isOpen={isDeleteOpen}
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={() => {
          setIsDeleteOpen(false);
          deleteUser.mutate(undefined, { onSuccess: goToUserList });
        }}
        description="userDetails.actions.deleteDescription"
        title="userDetails.actions.deleteTitle"
      />

      <OvResetPasswordDialog
        isResetPasswordDialogOpen={isResetPasswordOpen}
        onCancel={() => setIsResetPasswordOpen(false)}
        email={user.email}
      />

      <OvConfirmationDialog
        icon={<DialogIcon as={DeleteIcon} />}
        isOpen={isDeactivateOpen}
        onCancel={() => setIsDeactivateOpen(false)}
        onConfirm={() => {
          setIsDeactivateOpen(false);
          setIsConfirmInputOpen(true);
        }}
        title="userDetails.actions.deactivateTitle"
        description="userDetails.actions.deactivateDescription"
      />

      <OvInfoModal
        isOpen={isConfirmInputOpen}
        title={t('common.areYouSure')}
        onCancel={() => setIsConfirmInputOpen(false)}
        icon={<InfoIcon />}
      >
        <OvConfirmationInput
          expectedValue={user.email}
          label={t('userDetails.actions.deactivateConfirm')}
          ctaLabel={t('userDetails.actions.deactivateConfirmAction')}
          onMatch={() => {
            setIsConfirmInputOpen(false);
            deactivateUser.mutate(undefined, { onSuccess: goToUserList });
          }}
          caseSensitive={false}
        />
      </OvInfoModal>
    </Actions>
  );
};

export default OvUserRowActions;

const Actions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.125rem;
`;

const StyledIconButton = styled(IconButton)`
  && {
    width: 2rem;
    height: 2rem;
    padding: 0.375rem;
    color: ${Colours.OV_BASE};
  }
`;

const DialogIcon = styled.svg`
  && {
    display: block;
    box-sizing: content-box;
    width: 2rem;
    height: 2rem;
    margin: 1rem auto 0;
    padding: 0.5rem;
    border-radius: 50%;
    background: ${Colours.OV_BASE};
    color: ${Colours.OV_SEMI_LIGHT};
  }
`;
